import "@/App.css";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import TopBar from "@/components/sections/TopBar";
import Hero from "@/components/sections/Hero";
import WhatIsPadel from "@/components/sections/WhatIsPadel";
import Highlights from "@/components/sections/Highlights";
import About from "@/components/sections/About";
import Academy from "@/components/sections/Academy";
import Activities from "@/components/sections/Activities";
import PostGame from "@/components/sections/PostGame";
import Partners from "@/components/sections/Partners";
import Footer from "@/components/sections/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import CookieConsent from "@/components/CookieConsent";

// Lazy-loaded below-the-fold heavy section (Radix Select + Google Maps)
const Contact = lazy(() => import("@/components/sections/Contact"));
// Toaster (sonner) só é necessário após interação — não precisa de estar no bundle inicial
const Toaster = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

// Lazy-loaded secondary routes (code splitting)
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminTournaments = lazy(() => import("@/pages/admin/AdminTournaments"));
const AdminPartners = lazy(() => import("@/pages/admin/AdminPartners"));
const AdminHero = lazy(() => import("@/pages/admin/AdminHero"));
const AdminContact = lazy(() => import("@/pages/admin/AdminContact"));
const Privacidade = lazy(() => import("@/pages/Privacidade"));

function HomePage() {
  return (
    <div className="bg-[#0a0f1a] min-h-screen" data-testid="home-page">
      <TopBar />
      <Hero />
      <Highlights />
      <WhatIsPadel />
      <About />
      <Academy />
      <Activities />
      <PostGame />
      <Partners />
      <DeferredContact />
      <Footer />
      <WhatsAppButton />
      <CookieConsent />
    </div>
  );
}

function RouteFallback() {
  return <div className="bg-[#0a0f1a] min-h-screen" />;
}

// Só descarrega o chunk da secção Contactos quando o utilizador se aproxima dela
function DeferredContact() {
  const ref = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow(true);
          obs.disconnect();
        }
      },
      { rootMargin: "800px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {show ? (
        <Suspense fallback={<div id="contacts" className="min-h-[400px]" />}>
          <Contact />
        </Suspense>
      ) : (
        <div id="contacts" className="min-h-[400px]" />
      )}
    </div>
  );
}

// Toaster (sonner) carregado em idle, fora do caminho crítico
function DeferredToaster() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const cb = () => setReady(true);
    if ("requestIdleCallback" in window) window.requestIdleCallback(cb, { timeout: 3000 });
    else setTimeout(cb, 2000);
  }, []);
  return ready ? (
    <Suspense fallback={null}>
      <Toaster position="top-right" richColors />
    </Suspense>
  ) : null;
}

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacidade" element={<Privacidade />} />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="tournaments" element={<AdminTournaments />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="hero" element={<AdminHero />} />
            <Route path="contact" element={<AdminContact />} />
          </Route>
        </Routes>
      </Suspense>
      <DeferredToaster />
    </BrowserRouter>
  );
}

export default App;
